/**
 * DefaultScrollerUIAdapter.js
 *
 * UI adapter used by the visualizer to render swatch grids (colors/textures)
 * and to wire drag-and-drop behavior. This module intentionally owns DOM
 * manipulation so rendering remains centralized and consistent.
 *
 * Call flow overview:
 * - Visualizer boot creates this adapter and stores it on Application.
 * - Swatch panel modules call one of:
 *   - renderColors() when a color CSV is selected.
 *   - renderTextures() when a texture CSV is selected.
 *   - renderTextureSwatches() when a texture-swatch dataset is selected.
 * - After each render, the caller invokes initDragAndDrop().
 * - setTitleVisibility/resetTitleVisibility can be used to toggle labels
 *   (e.g. during search) and then re-render the current view.
 */

import { OverlayScrollbars } from 'overlayscrollbars'
import 'overlayscrollbars/styles/overlayscrollbars.css'
import '../../css/scrollbar-dark-theme.css'
import '../../css/scrollbar-light-theme.css'
import { DOM } from './DOM.js'
import { Selector } from './Selectors.js'
import PaintingListener from '../listeners/PaintingListener.js'

/**
 * Factory: createDefaultScrollerUIAdapter
 *
 * All behavior is driven by these options (constructor-level),
 * not per-call flags.
 */
/**
 * Create a UI adapter instance used by the visualizer.
 *
 * @param {Object} [options]
 * @param {boolean} [options.enableDragAndDrop=true] - Toggle drag-and-drop for swatches.
 * @param {boolean} [options.showTitlesColors=true] - Show labels on color swatches.
 * @param {boolean} [options.showTitlesTextures=true] - Show labels on texture swatches.
 * @param {boolean} [options.showTitlesTextureSwatches=true] - Show labels on texture-swatch tiles.
 * @param {boolean} [options.showPageHeaderColors=true] - Show headers for color pages.
 * @param {boolean} [options.showPageHeaderTextures=true] - Show headers for texture pages.
 * @param {boolean} [options.showPageHeaderTextureSwatches=true] - Show headers for texture-swatch pages.
 * @param {string} [options.scrollbarTheme='scrollbar-dark-theme'] - OverlayScrollbars theme class.
 * @returns {Object} Adapter API used by swatch panel modules.
 */
export function createDefaultScrollerUIAdapter({
    enableDragAndDrop = true,
    showTitlesColors = true,
    showTitlesTextures = true,
    showTitlesTextureSwatches = true,
    showPageHeaderColors = true,
    showPageHeaderTextures = true,
    showPageHeaderTextureSwatches = true,
    scrollbarTheme = 'scrollbar-dark-theme', // 'scrollbar-dark-theme' and 'scrollbar-light-theme'
} = {}) {
    let scrollbarColor = null
    let scrollbarEffect = null
    const lazyTextureObservers = {}
    // Snapshot of the initial title visibility settings (used for resets).
    const defaultTitleVisibility = {
        colors: showTitlesColors,
        textures: showTitlesTextures,
        textureSwatches: showTitlesTextureSwatches,
    }
    // Mutable visibility state that can be overridden at runtime (e.g. search mode).
    let titleVisibility = { ...defaultTitleVisibility }

    /**
     * Update the runtime visibility of swatch labels.
     *
     * @param {Object} [opts]
     * @param {boolean} [opts.colors] - Show labels in color grid.
     * @param {boolean} [opts.textures] - Show labels in texture grid.
     * @param {boolean} [opts.textureSwatches] - Show labels in texture-swatch grid.
     * @returns {boolean} True if any visibility flag changed.
     */
    function setTitleVisibility({
        colors = titleVisibility.colors,
        textures = titleVisibility.textures,
        textureSwatches = titleVisibility.textureSwatches,
    } = {}) {
        // "next" is the candidate visibility state (merged from passed overrides).
        // We compare it against the current state so we only re-render when needed.
        const next = { colors, textures, textureSwatches }
        const changed =
            next.colors !== titleVisibility.colors ||
            next.textures !== titleVisibility.textures ||
            next.textureSwatches !== titleVisibility.textureSwatches

        if (changed) {
            titleVisibility = { ...next }
        }

        return changed
    }

    /**
     * Reset label visibility to the original adapter options.
     *
     * @returns {boolean} True if any visibility flag changed.
     */
    function resetTitleVisibility() {
        return setTitleVisibility(defaultTitleVisibility)
    }

    /**
     * Read the current label visibility state.
     *
     * @returns {{colors: boolean, textures: boolean, textureSwatches: boolean}}
     */
    function getTitleVisibility() {
        return { ...titleVisibility }
    }

    /**
     * Lazily instantiate OverlayScrollbars so we only pay the cost once.
     *
     * @returns {void}
     */
    function ensureScrollbars() {
        if (!scrollbarColor) {
            scrollbarColor = OverlayScrollbars(DOM.get(Selector.colorchartselectors), {
                scrollbars: {
                    theme: scrollbarTheme,
                },
            })
        }
        if (!scrollbarEffect) {
            scrollbarEffect = OverlayScrollbars(DOM.get(Selector.textureswatchselectors), {
                scrollbars: {
                    theme: scrollbarTheme,
                },
            })
        }
    }

    /**
     * Lazy-load texture tile backgrounds for the currently rendered scroller.
     *
     * Texture tiles are CSS backgrounds, so native `<img loading="lazy">` cannot help here.
     * The renderer keeps the image URL in `data-texture-src`; this helper applies
     * `background-image` only when a tile is close to the OverlayScrollbars viewport.
     *
     * @param {HTMLElement} contentEl - OverlayScrollbars content element containing the rendered texture tiles.
     * @param {HTMLElement} rootEl - Scroll viewport used by IntersectionObserver.
     * @param {string} selector - Stable container selector used to replace the previous observer after re-render.
     * @returns {void}
     */
    function loadVisibleTextureBackgrounds(contentEl, rootEl, selector) {
        // The panel re-renders when changing effects/searching; stop watching stale tiles first.
        lazyTextureObservers[selector]?.disconnect()

        const tiles = contentEl.querySelectorAll('[data-texture-src]')
        if (!tiles.length) return

        const load = (tile) => {
            tile.style.backgroundImage = `url('${tile.dataset.textureSrc}')`
        }

        if (typeof IntersectionObserver !== 'function') {
            tiles.forEach(load)
            return
        }

        // Use the scroller viewport as root so hidden rows do not start image requests.
        const observer = new IntersectionObserver(
            (entries, self) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return
                    load(entry.target)
                    self.unobserve(entry.target)
                })
            },
            {
                root: rootEl,
                // Small preload buffer avoids blank tiles while scrolling quickly.
                rootMargin: '160px 0px',
                threshold: 0.01,
            },
        )

        tiles.forEach((tile) => observer.observe(tile))
        lazyTextureObservers[selector] = observer
    }

    /**
     * Remove draggable-related attributes/classes without altering layout.
     *
     * @param {Document|HTMLElement} [root=document] - Root to search for draggable elements.
     * @returns {void}
     */
    function stripDragAttributes(root = document) {
        $(root)
            .find('.js-draggable, .ui-draggable, .ui-draggable-handle, .js-draggable-drag')
            .each(function () {
                // Remove only the draggable attribute and runtime drag state; keep classes for appearance.
                $(this).removeAttr('draggable').removeClass('js-draggable-drag')
            })
    }

    /**
     * Destroy jQuery UI draggables if present; fall back to stripping attributes.
     *
     * @returns {void}
     */
    function destroyDraggables() {
        if (typeof $.fn.draggable !== 'function') {
            stripDragAttributes()
            return
        }

        $('.js-draggable, .ui-draggable, .ui-draggable-handle').each(function () {
            const $el = $(this)
            if ($el.data('ui-draggable')) {
                $el.draggable('destroy')
            }
        })

        stripDragAttributes()
    }

    /**
     * Enable/disable drag & drop based on the adapter option.
     *
     * @returns {void}
     */
    function initDragAndDrop() {
        if (!enableDragAndDrop) {
            destroyDraggables()
            return
        }

        PaintingListener.addTouchEventToDropColor()

        if (typeof $.fn.draggable !== 'function') {
            return
        }

        $('.js-draggable').draggable(
            {
                scroll: false,
                cursor: 'move',
                helper: 'clone',
                appendTo: 'body',
                start(event, ui) {
                    $(ui.helper).removeClass('js-draggable').addClass('js-draggable-drag')

                    const instance = $(this).draggable('instance')
                    if (instance && instance.offset) {
                        instance.offset.click = {
                            left: Math.floor(ui.helper.width() / 2),
                            top: Math.floor(ui.helper.height() / 2),
                        }
                    }
                },
            },
            { passive: true },
        )
    }

    /**
     * Render a single color tile or empty slot.
     *
     * @param {Object|null} color - Color view-model entry or null for empty slot.
     * @param {boolean} showTitles - Whether to render the label.
     * @returns {string} HTML for a color slot.
     */
    function renderColorSlot(color, showTitles) {
        if (!color) {
            return `
                <div class="cbox cbox-ratio-color-empty">
                    <div class="flex-item-empty"></div>
                </div>
            `
        }

        const { r, g, b } = color.rgb
        // Class stays for styling/hooks; the draggable attribute is what actually toggles behavior.
        const draggableAttr = enableDragAndDrop ? 'draggable="true"' : ''
        const dragClass = 'js-draggable'
        const swatchAttr = Number.isFinite(Number(color.swatchId)) ? `data-swatch-id="${color.swatchId}"` : ''

        return `
            <div class="cbox cbox-ratio-color">
                <div id="${color.id}"
                     ${draggableAttr}
                     ${swatchAttr}
                     class="${dragClass} flex-sub-item-texture clickable-color"
                     style="background-color: rgb(${r}, ${g}, ${b});">
                    ${showTitles ? `<div class="swatchcolor">${color.label}</div>` : ''}
                </div>
            </div>
        `
    }

    /**
     * Render a single texture tile or empty slot.
     *
     * @param {Object|null} tex - Texture view-model entry or null for empty slot.
     * @param {boolean} showTitles - Whether to render the label.
     * @param {boolean} [isSwatch=false] - Whether the texture belongs to the texture-swatch grid.
     * @returns {string} HTML for a texture slot.
     */
    function renderTextureSlot(tex, showTitles, isSwatch = false) {
        if (!tex) {
            return `
                <div class="cbox cbox-ratio-texture-empty">
                    <div class="texture${isSwatch ? ' texture-swatch' : ''} flex-item-empty"></div>
                </div>
            `
        }

        const id = isSwatch ? `texture-swatch${tex.id}` : tex.id
        // Class stays for styling/hooks; the draggable attribute is what actually toggles behavior.
        const draggableAttr = enableDragAndDrop ? 'draggable="true"' : ''
        const dragClass = 'js-draggable'
        const swatchAttr = Number.isFinite(Number(tex.swatchId)) ? `data-swatch-id="${tex.swatchId}"` : ''
        const textureSwatchAttr = Number.isFinite(Number(tex.textureSwatchId))
            ? `data-texture-swatch-id="${tex.textureSwatchId}"`
            : ''

        return `
            <div class="cbox cbox-ratio-texture">
                <div id="${id}"
                     ${draggableAttr}
                     ${swatchAttr}
                     ${textureSwatchAttr}
                     data-texture-src="${tex.imagePath || ''}"
                     class="${dragClass} texture${isSwatch ? ' texture-swatch' : ''} flex-sub-item-texture clickable-color"
                     style="">
                    ${showTitles ? `<div class="swatchcolor">${tex.label}</div>` : ''}
                </div>
            </div>
        `
    }

    /**
     * Resolve the active content element (OverlayScrollbars vs raw container).
     *
     * @param {Object|null} scrollbarInstance - OverlayScrollbars instance or null.
     * @param {string} selector - Fallback selector for the raw container.
     * @returns {HTMLElement} Container element for rendered HTML.
     */
    function getContentElement(scrollbarInstance, selector) {
        return scrollbarInstance?.elements().content || DOM.get(selector)
    }

    /**
     * Build the HTML for a list of pages using a provided slot renderer.
     *
     * @param {Object} params
     * @param {Array<Object>} params.viewModel - Page view-model array.
     * @param {string} params.listClass - CSS class for the <ul>.
     * @param {boolean} params.headerEnabled - Whether to render page headers.
     * @param {boolean} [params.isTouch=false] - Touch device hint for layout tweaks.
     * @param {Function} params.slotRenderer - (slot, showTitles) => html.
     * @param {boolean} params.showTitles - Whether to render labels in slots.
     * @returns {string} HTML for all pages.
     */
    function buildSwatchListHtml({
        viewModel,
        listClass,
        headerEnabled,
        isTouch = false,
        slotRenderer,
        showTitles,
    }) {
        return viewModel
            .map((page) => {
                const headerHtml = headerEnabled ? `<div class="cbox-header"><div>${page.pageName}</div></div>` : ''
                const touchClass = isTouch ? ' flex-container-color-30-percent' : ''

                return `
                    <ul class="${listClass}${touchClass}">
                        ${headerHtml}
                        ${page.slots.map((slot) => slotRenderer(slot, showTitles)).join('')}
                    </ul>
                `
            })
            .join('')
    }

    /**
     * Shared render pipeline for the three swatch grids.
     *
     * @param {Object} params
     * @param {Array<Object>} params.viewModel - Page view-model array.
     * @param {string} params.listClass - CSS class for the <ul>.
     * @param {boolean} params.headerEnabled - Whether to render page headers.
     * @param {boolean} [params.isTouch=false] - Touch device hint for layout tweaks.
     * @param {Function} params.slotRenderer - (slot, showTitles) => html.
     * @param {boolean} params.showTitles - Whether to render labels in slots.
     * @param {Function} params.getScrollbarInstance - Getter that returns the current OverlayScrollbars instance.
     * @param {string} params.selector - Fallback selector for the raw container.
     * @returns {HTMLElement} Container element used for rendering.
     */
    function renderList({
        viewModel,
        listClass,
        headerEnabled,
        isTouch = false,
        slotRenderer,
        showTitles,
        getScrollbarInstance,
        selector,
    }) {
        ensureScrollbars()
        const html = buildSwatchListHtml({
            viewModel,
            listClass,
            headerEnabled,
            isTouch,
            slotRenderer,
            showTitles,
        })

        // If OverlayScrollbars is active, write into its content element.
        const scrollbarInstance = getScrollbarInstance?.()
        const contentEl = getContentElement(scrollbarInstance, selector)
        contentEl.innerHTML = html

        if (!enableDragAndDrop) {
            stripDragAttributes(contentEl)
        }

        loadVisibleTextureBackgrounds(contentEl, scrollbarInstance?.elements().viewport || DOM.get(selector), selector)

        return contentEl
    }

    /**
     * Render the color swatch grid (main selector).
     *
     * @param {Object} [params]
     * @param {Array<Object>} [params.viewModel=[]] - Page data array for rendering.
     * @param {boolean} [params.hasTextureSwatches=false] - Whether texture swatches exist.
     * @param {boolean} [params.isTouch=false] - Touch device hint for layout tweaks.
     * @returns {void}
     */
    function renderColors({ viewModel = [], hasTextureSwatches = false, isTouch = false } = {}) {
        renderList({
            viewModel,
            listClass: 'flex-container-color',
            headerEnabled: showPageHeaderColors,
            isTouch,
            slotRenderer: renderColorSlot,
            showTitles: titleVisibility.colors,
            getScrollbarInstance: () => scrollbarColor,
            selector: Selector.colorchartselectors,
        })

        if (!hasTextureSwatches) {
            DOM.get(Selector.colorchartselectors).classList.replace('col-lg-6', 'col-lg-12')
        }
    }

    /**
     * Render the texture swatch grid in the main selector.
     *
     * @param {Object} [params]
     * @param {Array<Object>} [params.viewModel=[]] - Page data array for rendering.
     * @returns {void}
     */
    function renderTextures({ viewModel = [] } = {}) {
        renderList({
            viewModel,
            listClass: 'flex-container-texture',
            headerEnabled: showPageHeaderTextures,
            slotRenderer: renderTextureSlot,
            showTitles: titleVisibility.textures,
            getScrollbarInstance: () => scrollbarColor,
            selector: Selector.colorchartselectors,
        })

    }

    /**
     * Render the secondary texture-swatch grid.
     *
     * @param {Object} [params]
     * @param {Array<Object>} [params.viewModel=[]] - Page data array for rendering.
     * @returns {void}
     */
    function renderTextureSwatches({ viewModel = [] } = {}) {
        /**
         * Render helper for texture-swatch slots.
         *
         * @param {Object|null} slot - Texture slot view-model entry.
         * @param {boolean} showTitles - Whether to render the label.
         * @returns {string} HTML for a texture-swatch slot.
         */
        const renderTextureSwatchSlot = (slot, showTitles) => renderTextureSlot(slot, showTitles, true)

        renderList({
            viewModel,
            listClass: 'flex-container-effect',
            headerEnabled: showPageHeaderTextureSwatches,
            slotRenderer: renderTextureSwatchSlot,
            showTitles: titleVisibility.textureSwatches,
            getScrollbarInstance: () => scrollbarEffect,
            selector: Selector.textureswatchselectors,
        })

    }

    return {
        renderColors,
        renderTextures,
        renderTextureSwatches,
        initDragAndDrop,
        // Update title visibility for swatch labels. Returns true if any value changed.
        setTitleVisibility,
        // Reset title visibility to the initial adapter options.
        resetTitleVisibility,
        // Inspect the current title visibility values.
        getTitleVisibility,
    }
}

// optional, but harmless: default export = same factory
export default createDefaultScrollerUIAdapter
